import { closeModal } from "../../store/slices/modals";
import { useDispatch } from "react-redux";
import { useTexts } from "../../hooks";
import store from "../../store";
import { SOCIAL_CASINO_LANG } from "../../commander/Types";
import { ModalProps } from "./Modal";
import Message from "./Message";

export type ErrorType = {
  code?: number;
  ErrorCode?: number;
  title?: string;
  message?: string;
  data?: any;
} | null;

type ErrorConfig = {
  code: number | number[];
  text?: string;
  reload: boolean;
  customMessage?: (
    e: any,
    translations?: any
  ) => { title?: string; message?: string };
};

export const getErrorMessage = (error: ErrorType, translations: any) => {
  const cfg = getErorrConfig(error?.code);

  return (
    translations[cfg?.text ?? ""] ??
    error?.message ??
    `Connection failure. Response code ${error?.code ?? -1}`
  );
};

const getErorrConfig = (_code?: number, configs = ERROR_CONFIGS) => {
  const code = _code ?? -1;
  return configs.find((c) => {
    return code === c.code || (Array.isArray(c.code) && c.code.includes(code));
  });
};

const getHandler = (
  error: ErrorType,
  translations: any,
  closeHandler: () => void,
) => {
  const code = error?.code ?? -1;
  const config = getErorrConfig(code) as ErrorConfig;
  const message = getErrorMessage(error, translations);

  const onClose =
    !config || (config && config.reload)
      ? () => window.location.reload()
      : closeHandler;

  const defaultData = { message, onClose };
  const customData = config?.customMessage
    ? config.customMessage(error, translations)
    : {};

  return { ...defaultData, ...customData };
};

const ERROR_CONFIGS = [
  { code: 0, text: "errornoconnection", reload: true },
  { code: [112], text: "errormaxlosses", reload: true },
  { code: 400, text: "errorbadrequest", reload: true },
  { code: [401, 457], text: "errornoconnection", reload: true },
  { code: 403, text: "errorforbidden", reload: true },
  { code: 404, text: "errornotfound", reload: true },
  { code: 406, text: "errornotacceptable", reload: true },
  { code: [412, 101], text: "errorsequence", reload: true },
  { code: 428, text: "errorsequenceprecondition", reload: true },
  { code: 429, text: "errortoomanyrequests", reload: true },
  { code: [461, 612], text: "errordailytimelimit", reload: false },
  { code: [462, 611], text: "errorweeklytimelimit", reload: false },
  { code: [463, 610], text: "errormonthlytimelimit", reload: false },
  { code: [451, 464, 500], text: "errornetworkerror", reload: true },
  { code: [503, 608, 609], text: "errorgameunavailable", reload: true },
  { code: [102, 408, 504], text: "errortimeout", reload: false },
  {
    code: 606,
    reload: false,
    customMessage: (e: any) => {
      const title = e?.data?.errorparameters?.errorDetails?.title;
      const message =
        e?.data?.errorparameters?.errorDetails?.message ?? "Unknown Error";

      return { title, message };
    },
  },
  // some social casino wanted it some special message for the "no funds error", hence this crap
  {
    code: [450, 8004, 6025],
    reload: false,
    customMessage: (_e: any, translations: any) => {
      const state = store.getState();
      const message = "errornofunds";
      const title =
        state?.details?.language === SOCIAL_CASINO_LANG
          ? translations.errornofundstitle
          : "";

      return { title, message };
    },
  },
  {
    code: [607],
    text: "errormaxlosses",
    reload: true,
    customMessage: (e: ErrorType & { data?: string }) => {
      // NOTE that the "data" property is set in the game-manager
      // the actual property returned from the servers is "error_desc"
      return e.data ? { message: e.data } : {};
    },
  },
];

const Error: React.FC<ModalProps & ErrorType> = (props) => {
  const { common } = useTexts();
  const dispatch = useDispatch();
  const closeHandler = () => dispatch(closeModal());
  const handler = getHandler(props, common, closeHandler);
  const title = handler?.title ?? props.title;

  return (
    <Message
      shown={props.shown}
      icon="Info"
      title={title}
      type="error"
      message={handler.message}
      onClose={handler.onClose}
    />
  );
};

export default Error;
